// ─────────────────────────────────────────────
// Audit label helpers
//
// Maps raw audit action / resourceType values (as stored in the
// audit DB) to translation keys. Unknown values fall back to the
// raw string so new backend vocabulary degrades gracefully
// instead of leaking translation keys into the UI.
// ─────────────────────────────────────────────

import type { TranslateParams } from "@/lib/i18n";

type TranslateFn = (key: string, params?: TranslateParams) => string;

const ACTION_KEYS: Record<string, string> = {
  create: "auditLogs.actions.create",
  update: "auditLogs.actions.update",
  delete: "auditLogs.actions.delete",
  publish: "auditLogs.actions.publish",
  submit: "auditLogs.actions.submit",
  review: "auditLogs.actions.review",
  approve: "auditLogs.actions.approve",
  reject: "auditLogs.actions.reject",
  archive: "auditLogs.actions.archive",
  restore: "auditLogs.actions.restore",
  read: "auditLogs.actions.read",
  score: "auditLogs.actions.score",
  add_comment: "auditLogs.actions.add_comment",
  review_comment: "auditLogs.actions.review_comment",
  delete_review_comment: "auditLogs.actions.delete_review_comment",
  add_dependency: "auditLogs.actions.add_dependency",
  remove_dependency: "auditLogs.actions.remove_dependency",
  request_pin: "auditLogs.actions.request_pin",
  verify_pin: "auditLogs.actions.verify_pin",
  upsert_responses: "auditLogs.actions.upsert_responses",
};

const RESOURCE_KEYS: Record<string, string> = {
  ApiKey: "auditLogs.resources.ApiKey",
  Assessment: "auditLogs.resources.Assessment",
  ConditionalRule: "auditLogs.resources.ConditionalRule",
  ControlMapping: "auditLogs.resources.ControlMapping",
  Document: "auditLogs.resources.Document",
  Domain: "auditLogs.resources.Domain",
  Entity: "auditLogs.resources.Entity",
  Posture: "auditLogs.resources.Posture",
  Question: "auditLogs.resources.Question",
  QuestionPrefill: "auditLogs.resources.QuestionPrefill",
  Schema: "auditLogs.resources.Schema",
  SchemaVersion: "auditLogs.resources.SchemaVersion",
  ShareToken: "auditLogs.resources.ShareToken",
};

export function auditActionLabel(t: TranslateFn, action: string): string {
  const key = ACTION_KEYS[action.toLowerCase()];
  return key ? t(key) : action;
}

export function auditResourceLabel(t: TranslateFn, resourceType: string): string {
  const key = RESOURCE_KEYS[resourceType];
  return key ? t(key) : resourceType;
}

/** Stored action values, for filter dropdowns (labels via auditActionLabel). */
export const AUDIT_FILTER_ACTIONS = Object.keys(ACTION_KEYS);

/** Stored resourceType values, for filter dropdowns (labels via auditResourceLabel). */
export const AUDIT_FILTER_RESOURCES = Object.keys(RESOURCE_KEYS);
